import * as Sentry from "@sentry/browser";
import moment from "moment";
import { Action } from "redux";
import { ThunkAction } from "redux-thunk";
import {
  addPrices,
  Booking,
  BookingInquiryMessage,
  Event,
  Inquiry,
  normalizeProposal,
  Payment,
  Revision,
  validateBooking,
  validatePayment
} from "sweet-global";
import firebase from "sweet-web/firebase";
import * as selectors from "sweet-web/redux/selectors";
import { RootState } from "../index";
import {
  BookingActionTypes,
  CONFIRM_BOOKING_FAILURE,
  CONFIRM_BOOKING_INIT,
  CONFIRM_BOOKING_SUCCESS,
  CREATE_BOOKING_FAILURE,
  CREATE_BOOKING_INIT,
  CREATE_BOOKING_SUCCESS,
  DELETE_BOOKING,
  DELETE_PAYMENT,
  DELETE_REVISION,
  EDIT_BOOKING,
  GET_BOOKING_REVISIONS_SUCCESS,
  GET_BOOKINGS_FAILURE,
  GET_BOOKINGS_INIT,
  GET_BOOKINGS_SUCCESS,
  GET_PAYMENTS_FAILURE,
  GET_PAYMENTS_INIT,
  GET_PAYMENTS_SUCCESS,
  LOAD_EDITABLE_BOOKING,
  MAKE_PAYMENT_FAILURE,
  MAKE_PAYMENT_INIT,
  MAKE_PAYMENT_SUCCESS,
  RESET_BOOKING,
  REVISE_BOOKING_FAILURE,
  REVISE_BOOKING_INIT,
  REVISE_BOOKING_SUCCESS,
  SET_EDITING_BOOKING,
  SUBMIT_PROPOSAL_FAILURE,
  SUBMIT_PROPOSAL_INIT,
  SUBMIT_PROPOSAL_SUCCESS
} from "./types";

const firestore = firebase.firestore();

export const resetBooking = (): BookingActionTypes => {
  return { type: RESET_BOOKING };
};

export const setEditingBooking = (editing: boolean): BookingActionTypes => {
  return { type: SET_EDITING_BOOKING, editing };
};

export const loadEditableBooking = (
  booking: (Inquiry & Event) | null
): BookingActionTypes => {
  return { type: LOAD_EDITABLE_BOOKING, booking };
};

export const editBooking = (
  changes: Partial<Inquiry & Event>
): BookingActionTypes => {
  return { type: EDIT_BOOKING, changes };
};

export const getBookingsInit = (): BookingActionTypes => {
  return { type: GET_BOOKINGS_INIT };
};

export const getBookingsSuccess = (bookings: {
  [bookingId: string]: Booking;
}): BookingActionTypes => {
  return { type: GET_BOOKINGS_SUCCESS, bookings };
};

export const getBookingRevisionsSuccess = (
  bookingId: string,
  revisions: { [revisionId: string]: Revision }
): BookingActionTypes => {
  return { type: GET_BOOKING_REVISIONS_SUCCESS, bookingId, revisions };
};

export const getBookingsFailure = (error: Error): BookingActionTypes => {
  return { type: GET_BOOKINGS_FAILURE, error };
};

export const deleteBooking = (bookingId: string): BookingActionTypes => {
  return { type: DELETE_BOOKING, bookingId };
};

export const deleteRevision = (
  bookingId: string,
  revisionId: string
): BookingActionTypes => {
  return { type: DELETE_REVISION, bookingId, revisionId };
};

export const createBookingInit = (): BookingActionTypes => {
  return { type: CREATE_BOOKING_INIT };
};

export const createBookingSuccess = (): BookingActionTypes => {
  return { type: CREATE_BOOKING_SUCCESS };
};

export const createBookingFailure = (error: Error): BookingActionTypes => {
  return { type: CREATE_BOOKING_FAILURE, error };
};

export const reviseBookingInit = (): BookingActionTypes => {
  return { type: REVISE_BOOKING_INIT };
};

export const reviseBookingSuccess = (): BookingActionTypes => {
  return { type: REVISE_BOOKING_SUCCESS };
};

export const reviseBookingFailure = (error: Error): BookingActionTypes => {
  return { type: REVISE_BOOKING_FAILURE, error };
};

export const submitProposalInit = (): BookingActionTypes => {
  return { type: SUBMIT_PROPOSAL_INIT };
};

export const submitProposalSuccess = (): BookingActionTypes => {
  return { type: SUBMIT_PROPOSAL_SUCCESS };
};

export const submitProposalFailure = (error: Error): BookingActionTypes => {
  return { type: SUBMIT_PROPOSAL_FAILURE, error };
};

export const confirmBookingInit = (): BookingActionTypes => {
  return { type: CONFIRM_BOOKING_INIT };
};

export const confirmBookingSuccess = (
  bookingId: string
): BookingActionTypes => {
  return { type: CONFIRM_BOOKING_SUCCESS, bookingId };
};

export const confirmBookingFailure = (error: Error): BookingActionTypes => {
  return { type: CONFIRM_BOOKING_FAILURE, error };
};

export const getPaymentsInit = (): BookingActionTypes => {
  return { type: GET_PAYMENTS_INIT };
};

export const getPaymentsSuccess = (payments: {
  [paymentId: string]: Payment;
}): BookingActionTypes => {
  return { type: GET_PAYMENTS_SUCCESS, payments };
};

export const deletePayment = (paymentId: string): BookingActionTypes => {
  return { type: DELETE_PAYMENT, paymentId };
};

export const getPaymentsFailure = (error: Error): BookingActionTypes => {
  return { type: GET_PAYMENTS_FAILURE, error };
};

export const makePaymentInit = (): BookingActionTypes => {
  return { type: MAKE_PAYMENT_INIT };
};

export const makePaymentSuccess = (): BookingActionTypes => {
  return { type: MAKE_PAYMENT_SUCCESS };
};

export const makePaymentFailure = (error: Error): BookingActionTypes => {
  return { type: MAKE_PAYMENT_FAILURE, error };
};

export const GetBookings = (): ThunkAction<
  Promise<() => void>,
  RootState,
  null,
  Action<string>
> => async (dispatch, getState) => {
  try {
    dispatch(getBookingsInit());

    const listeners: {
      booking?: () => void;
      [bookingId: string]: () => void;
    } = {};

    const state = getState();

    if (!state.auth.uid) throw new Error("no authorized user");

    const userType = selectors.userType(state);

    let bookingQuery = null;
    if (userType === "VENUE") {
      const myVenues = selectors.myVenues(state);
      for (const venueId of Object.keys(myVenues)) {
        bookingQuery = firestore
          .collection("bookings")
          .where("venue", "==", venueId);
      }
    } else {
      bookingQuery = firestore
        .collection("bookings")
        .where("user", "==", state.auth.uid);
    }

    const bookings = Object.assign({}, selectors.bookings(state));

    listeners.booking = bookingQuery.onSnapshot(snap => {
      const changes = snap.docChanges();

      changes.forEach(change => {
        if (change.type === "added") {
          bookings[change.doc.id] = {
            ...change.doc.data(),
            id: change.doc.id
          } as Booking;

          if (
            listeners[change.doc.id] &&
            typeof listeners[change.doc.id] === "function"
          )
            listeners[change.doc.id]();
          listeners[change.doc.id] = firestore
            .collection("bookings")
            .doc(change.doc.id)
            .collection("revisions")
            .onSnapshot(snap => {
              const changes = snap.docChanges();
              const newRevisions: { [revisionId: string]: Revision } = {};
              changes.forEach(change => {
                if (change.type === "added") {
                  newRevisions[change.doc.id] = change.doc.data() as Revision;
                }
                if (change.type === "modified") {
                  newRevisions[change.doc.id] = change.doc.data() as Revision;
                }
                if (change.type === "removed") {
                  dispatch(deleteRevision(change.doc.id, change.doc.id));
                }
              });
              if (Object.keys(newRevisions).length > 0)
                dispatch(
                  getBookingRevisionsSuccess(change.doc.id, newRevisions)
                );
            });
        }
        if (change.type === "modified") {
          bookings[change.doc.id] = {
            ...change.doc.data(),
            id: change.doc.id
          } as Booking;
        }
        if (change.type === "removed") {
          delete bookings[change.doc.id];
          dispatch(deleteBooking(change.doc.id));
        }
      });

      dispatch(getBookingsSuccess(bookings));
    });

    return () => {
      Object.values(listeners).forEach(listener => {
        if (typeof listener === "function") listener();
      });
    };
  } catch (error) {
    Sentry.captureException(error);
    dispatch(getBookingsFailure(error));

    return () => {
      // no listener
    };
  }
};

export const SubmitProposal = (
  bookingId: string
): ThunkAction<Promise<void>, RootState, null, Action<string>> => async (
  dispatch,
  getState
) => {
  try {
    dispatch(submitProposalInit());

    const state = getState();

    if (!state.auth.uid) throw new Error("no authorized user");

    const editedBooking = selectors.editableBooking(state);

    let booking: Booking = editedBooking;
    if (editedBooking.status === "INQUIRY") {
      booking = normalizeProposal(editedBooking);
    } else {
      throw new Error("submit proposal on inquiry");
    }

    const bookingValidation = validateBooking(booking);

    if (!bookingValidation.valid)
      throw new Error(
        `Invalid booking – please complete: ${bookingValidation.errors.join(
          ", "
        )}`
      );

    await firestore
      .collection("bookings")
      .doc(bookingId)
      .update(booking);

    dispatch(submitProposalSuccess());
  } catch (error) {
    Sentry.captureException(error);
    dispatch(submitProposalFailure(error));
  }
};

export const ReviseBooking = (
  bookingId: string
): ThunkAction<Promise<void>, RootState, null, Action<string>> => async (
  dispatch,
  getState
) => {
  try {
    dispatch(reviseBookingInit());

    const state = getState();

    if (!state.auth.uid) throw new Error("no authorized user");

    const bookingEntity = selectors.getBooking(bookingId)(state);

    if (bookingEntity.status === "INQUIRY") {
      // update inquiry
      const processedInquiry = Object.assign(
        {},
        selectors.editableBooking(state)
      );
      delete processedInquiry.deposit;
      delete processedInquiry.total;
      const newBooking = processedInquiry as Booking;

      const bookingValidation = validateBooking(newBooking);

      if (!bookingValidation.valid)
        throw new Error(
          `Invalid inquiry – please complete: ${bookingValidation.errors.join(
            ", "
          )}`
        );

      await firestore
        .collection("bookings")
        .doc(bookingId)
        .update(newBooking);
    } else {
      // submit revision which needs to be confirmed by other party
      // TODO
    }

    dispatch(reviseBookingSuccess());
  } catch (error) {
    Sentry.captureException(error);
    dispatch(reviseBookingFailure(error));
  }
};

export const CreateBooking = (
  booking: Booking
): ThunkAction<
  Promise<string | null>,
  RootState,
  null,
  Action<string>
> => async (dispatch, getState) => {
  try {
    dispatch(createBookingInit());

    const state = getState();

    if (!state.auth.uid) throw new Error("no authorized user");

    const bookingValidation = validateBooking(booking);

    if (!bookingValidation.valid)
      throw new Error(`Invalid booking – please fill out all fields`);

    const bookingDoc = await firestore.collection("bookings").add(booking);

    dispatch(createBookingSuccess());

    return bookingDoc.id;
  } catch (error) {
    Sentry.captureException(error);
    dispatch(createBookingFailure(error));
    throw error;
  }
};

export const DeleteBooking = (
  bookingId: string
): ThunkAction<Promise<void>, RootState, null, Action<string>> => async (
  dispatch,
  getState
) => {
  try {
    await firestore
      .collection("bookings")
      .doc(bookingId)
      .delete();

    dispatch(deleteBooking(bookingId));
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
};

export const CancelBooking = (
  bookingId: string
): ThunkAction<Promise<void>, RootState, null, Action<string>> => async (
  dispatch,
  getState
) => {
  try {
    const state = getState();

    const userType = selectors.userType(state);

    await firestore
      .collection("bookings")
      .doc(bookingId)
      .update({
        status: "CANCELLED",
        canceledBy: userType
      });
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
};

export const GetPayments = (): ThunkAction<
  Promise<() => void>,
  RootState,
  null,
  Action<string>
> => async (dispatch, getState) => {
  try {
    dispatch(getPaymentsInit());

    const state = getState();

    if (!state.auth.uid) throw new Error("no authorized user");

    let paymentsQuery = null;
    if (state.user.entity.type === "VENUE") {
      const myVenues = selectors.myVenues(state);
      for (const venueId of Object.keys(myVenues)) {
        paymentsQuery = firestore
          .collection("payments")
          .where("venue", "==", venueId);
      }
    } else {
      paymentsQuery = firestore
        .collection("payments")
        .where("user", "==", state.auth.uid);
    }

    const listener = paymentsQuery.onSnapshot(snap => {
      const changes = snap.docChanges();
      const newPayments: { [paymentId: string]: Payment } = {};

      changes.forEach(change => {
        if (change.type === "added") {
          newPayments[change.doc.id] = change.doc.data() as Payment;
        }
        if (change.type === "modified") {
          newPayments[change.doc.id] = change.doc.data() as Payment;
        }
        if (change.type === "removed") {
          dispatch(deletePayment(change.doc.id));
        }
      });
      dispatch(getPaymentsSuccess(newPayments));
    });

    return () => {
      if (typeof listener === "function") listener();
    };
  } catch (error) {
    Sentry.captureException(error);
    dispatch(getBookingsFailure(error));

    return () => {
      // no listener
    };
  }
};

export const MakePayment = (
  bookingId: string,
  source?: string,
  bank?: Payment["bank"]
): ThunkAction<
  Promise<string | null>,
  RootState,
  null,
  Action<string>
> => async (dispatch, getState) => {
  try {
    dispatch(makePaymentInit());

    const state = getState();

    if (!state.auth.uid) throw new Error("no authorized user");

    const booking = selectors.getBooking(bookingId)(state);

    if (booking.status === "INQUIRY") {
      throw new Error("cannot pay for an unconfirmed event");
    }

    if (booking.deposited) {
      throw new Error(
        "already submitted deposit, payment will complete on the date of the event"
      );
    }

    const payment: Payment = {
      type: "DEPOSIT",
      createdTime: moment().unix(),
      user: booking.user,
      venue: booking.venue,
      booking: bookingId,
      executed: false
    };
    if (source) payment.source = source;
    if (bank) payment.bank = bank;

    const paymentValidation = validatePayment(payment);

    if (!paymentValidation.valid)
      throw new Error(`Invalid payment – please fill out all fields`);

    const paymentDoc = await firestore.collection("payments").add(payment);

    dispatch(makePaymentSuccess());

    return paymentDoc.id;
  } catch (error) {
    Sentry.captureException(error);
    dispatch(makePaymentFailure(error));
    throw error;
  }
};
